import React, { useId } from "react";
import {
  Divider,
  FormControlLabel,
  formControlLabelClasses,
  FormHelperText,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { getEventHandlerProps } from "~/utils/get-event-handler-props";
import { usePlayerActions } from "../../actions";
import {
  ControlsSlider,
  SourceTopicSelectV2,
  TagsControl,
  TopicName,
} from "../../components";
import { useLoadedPlayerTopics } from "../../hooks";
import type {
  PairedTopic,
  PointCloudTopicDescriptor,
  ThreeDPanel,
} from "../../panels";
import { constraints, usePairedTopics } from "../../panels";

export function ThreeDVisualizationControls({ panel }: { panel: ThreeDPanel }) {
  const id = useId();

  const topics = useLoadedPlayerTopics();

  const playerActions = usePlayerActions();

  const pairedTopics = usePairedTopics(panel);

  const tfStaticTopic =
    topics.find((topic) => topic.name === "/tf_static") ?? null;

  const toggleStaticTransformsHandlerProps = getEventHandlerProps(
    "onChange",
    tfStaticTopic != null &&
      function handleToggleStaticTransforms(): void {
        playerActions.toggleStaticTransforms(panel);
      },
  );

  function handleToggleRotationControls(): void {
    playerActions.toggleRotationControls(panel);
  }

  return (
    <>
      <FormControlLabel
        sx={{
          justifyContent: "space-between",
          marginInlineStart: 0,
        }}
        control={
          <Switch
            id={id}
            aria-describedby={`${id}-desc`}
            checked={
              toggleStaticTransformsHandlerProps.disabled
                ? false
                : panel.useStaticTransforms
            }
            {...toggleStaticTransformsHandlerProps}
          />
        }
        label="Use static transforms"
        labelPlacement="start"
      />
      <FormHelperText id={`${id}-desc`} sx={{ marginBlockEnd: 2 }}>
        {toggleStaticTransformsHandlerProps.disabled && (
          <>
            <TopicName monospace>/tf_static</TopicName> not in this log
          </>
        )}
      </FormHelperText>
      <FormControlLabel
        sx={{
          justifyContent: "space-between",
          marginInlineStart: 0,
          marginBlockEnd: 4,
        }}
        control={
          <Switch
            checked={panel.showRotationControls}
            onChange={handleToggleRotationControls}
          />
        }
        label="Rotation controls"
        labelPlacement="start"
      />
      {pairedTopics.map((pair, index) => (
        <React.Fragment key={pair.topic.id}>
          <PointCloudTopicControls panel={panel} pair={pair} />
          {index !== pairedTopics.length - 1 && <Divider />}
        </React.Fragment>
      ))}
      {pairedTopics.length !== constraints.threeDTopics.max && (
        <React.Fragment key={pairedTopics.length}>
          <Divider />
          <SourceTopicSelectV2 panel={panel} />
        </React.Fragment>
      )}
    </>
  );
}

function PointCloudTopicControls({
  panel,
  pair,
}: {
  panel: ThreeDPanel;
  pair: PairedTopic<PointCloudTopicDescriptor>;
}) {
  const playerActions = usePlayerActions();

  return (
    <>
      <Stack
        spacing={0.5}
        sx={{
          marginBlockEnd: 3,
          [`& .${formControlLabelClasses.root}`]: {
            marginInlineStart: 2,
          },
        }}
      >
        <SourceTopicSelectV2
          panel={panel}
          topic={pair.topic}
          disableClearing={panel.topics.length === constraints.threeDTopics.min}
        />
        <TagsControl topic={pair.topic} label="Show tags" size="small" />
      </Stack>
      <Typography>Point Size</Typography>
      <ControlsSlider
        value={pair.descriptor.size}
        onChange={playerActions.setPointCloudPointSize.bind(
          null,
          panel,
          pair.topic,
        )}
        min={constraints.pointSize.min}
        max={constraints.pointSize.max}
        step={constraints.pointSize.step}
      />
    </>
  );
}
